import React, { useEffect, useMemo, useState } from "react";
import { Progress } from "@radix-ui/themes";
import { useSearchParams } from "react-router-dom";

import { getPublicEmailBrowserContent } from "api/bMarketingApi";

const shellStyle = {
  minHeight: "100vh",
  background: "#e2e8f0",
  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
};

const statusShellStyle = {
  ...shellStyle,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  padding: "16px 24px",
  borderBottom: "1px solid rgba(148, 163, 184, 0.32)",
  background: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(12px)",
  position: "sticky",
  top: 0,
  zIndex: 10,
};

const cardStyle = {
  width: "100%",
  maxWidth: "640px",
  padding: "32px",
  borderRadius: "24px",
  background: "#ffffff",
  boxShadow: "0 24px 80px rgba(15, 23, 42, 0.16)",
  border: "1px solid rgba(148, 163, 184, 0.2)",
  textAlign: "center",
};

const loadingWrapStyle = {
  width: "100%",
  maxWidth: "360px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
};

const badgeStyle = {
  display: "inline-flex",
  padding: "6px 12px",
  borderRadius: 999,
  background: "rgba(255,255,255,0.75)",
  color: "#334155",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const HEAD_INJECTION = '<base target="_blank" /><meta name="referrer" content="no-referrer" />';

const injectBaseBlankTarget = (htmlContent) => {
  const html = String(htmlContent || "");
  if (!html) return "";

  if (/<base\b/i.test(html)) {
    return html;
  }

  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1>${HEAD_INJECTION}`);
  }

  return `${HEAD_INJECTION}${html}`;
};

function MarketingEmailBrowserPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [subject, setSubject] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [error, setError] = useState("");

  const templateId = (searchParams.get("template_id") || "").trim();
  const recipient = (searchParams.get("recipient") || "").trim();
  const token = (searchParams.get("token") || "").trim();

  useEffect(() => {
    let cancelled = false;

    const loadBrowserView = async () => {
      if (!templateId || !recipient || !token) {
        setStatus("invalid");
        setError("This browser-view link is missing information.");
        return;
      }

      setStatus("loading");
      setError("");

      try {
        const response = await getPublicEmailBrowserContent(templateId, recipient, token);
        if (cancelled) return;

        setSubject(response.data.subject || "Email browser view");
        setHtmlContent(response.data.html_content || "");
        setStatus("ready");
      } catch (requestError) {
        if (cancelled) return;

        setStatus("invalid");
        setError("Invalid or expired link");
      }
    };

    loadBrowserView();

    return () => {
      cancelled = true;
    };
  }, [recipient, templateId, token]);

  useEffect(() => {
    if (!subject) return;
    document.title = subject;
  }, [subject]);

  const iframeSrcDoc = useMemo(() => injectBaseBlankTarget(htmlContent), [htmlContent]);

  if (status === "loading") {
    return (
      <div style={statusShellStyle}>
        <div style={loadingWrapStyle}>
          <div style={badgeStyle}>
            email browser view
          </div>
          <div style={{ width: "100%" }}>
            <Progress size="1" duration="1" />
          </div>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: "#475569", textAlign: "center" }}>
            Loading email
          </p>
        </div>
      </div>
    );
  }

  if (status === "invalid") {
    return (
      <div style={statusShellStyle}>
        <div style={cardStyle}>
          <div style={badgeStyle}>
            email browser view
          </div>
          <h1 style={{ margin: "18px 0 10px", fontSize: 34, lineHeight: 1.05, color: "#0f172a" }}>
            Unable to load email
          </h1>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: "#475569" }}>
            {error || "Invalid or expired link"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      <div style={headerStyle}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b" }}>
            browser view
          </div>
          <div style={{ marginTop: 4, fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
            {subject || "Email browser view"}
          </div>
        </div>
      </div>

      <iframe
        title={subject || "Email browser view"}
        srcDoc={iframeSrcDoc}
        sandbox="allow-popups allow-popups-to-escape-sandbox allow-forms"
        referrerPolicy="no-referrer"
        style={{ width: "100%", minHeight: "calc(100vh - 74px)", border: 0, display: "block", background: "#ffffff" }}
      />
    </div>
  );
}

export default MarketingEmailBrowserPage;